export enum WorkOrderStatus {
  WAITING_ENTRY = "WAITING_ENTRY",
  ARRIVED_YARD = "ARRIVED_YARD",
  CHASSIS_PREP = "CHASSIS_PREP",
  READY_PRODUCTION = "READY_PRODUCTION",
  IN_PRODUCTION = "IN_PRODUCTION",
  PROD_TO_WELD = "PROD_TO_WELD",
  IN_WELDING = "IN_WELDING",
  WELD_TO_GRIND = "WELD_TO_GRIND",
  IN_GRINDING = "IN_GRINDING",
  GRIND_TO_PAINT = "GRIND_TO_PAINT",
  IN_PAINT = "IN_PAINT",
  PAINT_TO_FINISH = "PAINT_TO_FINISH",
  IN_FINISHES = "IN_FINISHES",
  DONE_NO_CURTAIN = "DONE_NO_CURTAIN",
  DONE_TEST_READY = "DONE_TEST_READY",
  RELEASE_READY = "RELEASE_READY",
}

export const WORK_ORDER_STATUS_VALUES: WorkOrderStatus[] = [
  WorkOrderStatus.WAITING_ENTRY,
  WorkOrderStatus.ARRIVED_YARD,
  WorkOrderStatus.CHASSIS_PREP,
  WorkOrderStatus.READY_PRODUCTION,
  WorkOrderStatus.IN_PRODUCTION,
  WorkOrderStatus.PROD_TO_WELD,
  WorkOrderStatus.IN_WELDING,
  WorkOrderStatus.WELD_TO_GRIND,
  WorkOrderStatus.IN_GRINDING,
  WorkOrderStatus.GRIND_TO_PAINT,
  WorkOrderStatus.IN_PAINT,
  WorkOrderStatus.PAINT_TO_FINISH,
  WorkOrderStatus.IN_FINISHES,
  WorkOrderStatus.DONE_NO_CURTAIN,
  WorkOrderStatus.DONE_TEST_READY,
  WorkOrderStatus.RELEASE_READY,
];

export type WorkOrderStatusFilter = WorkOrderStatus | "ALL";

export type WorkOrderPriority = "HIGH" | "MEDIUM" | "NORMAL";

export type WorkOrderStage =
  | "PAINT"
  | "METALWORK"
  | "QUALITY_CONTROL_STAGE";

export interface WorkOrderTableRow {
  id: number;
  workOrderId: string;
  status: WorkOrderStatus;
  customerName: string;
  vehicle: string;
  currentStage: WorkOrderStage;
  progress: number;
  priority: WorkOrderPriority;
}

export enum AxleStructureEnum {
  SIX_X2 = "6x2",
  EIGHT_X2 = "8x2",
  TEN_X2 = "10x2",
  TWELVE_X2 = "12x2",
}

export const AXLE_STRUCTURE_OPTIONS: AxleStructureEnum[] = [
  AxleStructureEnum.SIX_X2,
  AxleStructureEnum.EIGHT_X2,
  AxleStructureEnum.TEN_X2,
  AxleStructureEnum.TWELVE_X2,
];

export const BOX_TYPE_OPTIONS = [
  "OPEN_BOX",
  "CURTAIN_ROOF",
  "CLOSED_BOX",
  "REFRIGERATED",
] as const;
export type BoxTypeOption = (typeof BOX_TYPE_OPTIONS)[number];

const boxTypeOption = (value: string, optionName: string): [string, string] => [
  value,
  `WORK_ORDER.VEHICLE_DETAILS.BOX_TYPE_VALUES.${optionName}`,
];

export enum VehicleBoxType {
  BODY_TYPE = "BODY_TYPE",
  OPEN_BOX = "OPEN_BOX",
  CURTAIN_ROOF = "CURTAIN_ROOF",
  CLOSED_DRY_BOX = "CLOSED_DRY_BOX",
  OPEN_BOX_REGULAR_CRANE = "OPEN_BOX_REGULAR_CRANE",
  OPEN_BOX_BASKET_CRANE = "OPEN_BOX_BASKET_CRANE",
  INSULATED_CLOSED_BOX = "INSULATED_CLOSED_BOX",
  GAS_TRANSPORT = "GAS_TRANSPORT",
  CURTAIN_ROOF_LIBNER = "CURTAIN_ROOF_LIBNER",
  CURTAIN_ROOF_HYDRAULIC = "CURTAIN_ROOF_HYDRAULIC",
  CURTAIN_ROOF_INSULATED = "CURTAIN_ROOF_INSULATED",
  HARDWARE_GIANT_CRANE_BOX = "HARDWARE_GIANT_CRANE_BOX",
  HARDWARE_TOW_PLATE_WITH_CRANE = "HARDWARE_TOW_PLATE_WITH_CRANE",
  CATTLE_TRANSPORT = "CATTLE_TRANSPORT",
  DISASSEMBLY_ASSEMBLY_EXISTING_VEHICLE = "DISASSEMBLY_ASSEMBLY_EXISTING_VEHICLE",
  CURTAIN_ROOF_FOR_EXISTING = "CURTAIN_ROOF_FOR_EXISTING",
  HYDRAULIC_WALL_INSTALLATION = "HYDRAULIC_WALL_INSTALLATION",
  SKILLET = "SKILLET",
  REGULAR_SWAP_BODY = "REGULAR_SWAP_BODY",
  SWAP_CURTAIN_ROOF = "SWAP_CURTAIN_ROOF",
  SWAP_CLOSED_DRY = "SWAP_CLOSED_DRY",
  HYDRAULIC_WALL_DISASSEMBLY = "HYDRAULIC_WALL_DISASSEMBLY",
  PURPOSE_CHANGE = "PURPOSE_CHANGE",
  CURTAIN_ROOF_PNEUMATIC = "CURTAIN_ROOF_PNEUMATIC",
  COMBINED = "COMBINED",
  HYDRAULIC_ROOF_POULTRY_TRANSPORT = "HYDRAULIC_ROOF_POULTRY_TRANSPORT",
  OPEN_BOX_FORKLIFT_CARRIER = "OPEN_BOX_FORKLIFT_CARRIER",
  CURTAIN_ROOF_FORKLIFT_CARRIER = "CURTAIN_ROOF_FORKLIFT_CARRIER",
  OTHER = "OTHER",
  CANCELLED = "CANCELLED",
}

export const VEHICLE_BOX_TYPE_OPTIONS = [
  boxTypeOption(VehicleBoxType.BODY_TYPE, "BODY_TYPE"),
  boxTypeOption(VehicleBoxType.OPEN_BOX, "OPEN_BOX"),
  boxTypeOption(VehicleBoxType.CURTAIN_ROOF, "CURTAIN_ROOF"),
  boxTypeOption(VehicleBoxType.CLOSED_DRY_BOX, "CLOSED_DRY_BOX"),
  boxTypeOption(VehicleBoxType.OPEN_BOX_REGULAR_CRANE, "OPEN_BOX_REGULAR_CRANE"),
  boxTypeOption(VehicleBoxType.OPEN_BOX_BASKET_CRANE, "OPEN_BOX_BASKET_CRANE"),
  boxTypeOption(VehicleBoxType.INSULATED_CLOSED_BOX, "INSULATED_CLOSED_BOX"),
  boxTypeOption(VehicleBoxType.GAS_TRANSPORT, "GAS_TRANSPORT"),
  boxTypeOption(VehicleBoxType.CURTAIN_ROOF_LIBNER, "CURTAIN_ROOF_LIBNER"),
  boxTypeOption(VehicleBoxType.CURTAIN_ROOF_HYDRAULIC, "CURTAIN_ROOF_HYDRAULIC"),
  boxTypeOption(VehicleBoxType.CURTAIN_ROOF_INSULATED, "CURTAIN_ROOF_INSULATED"),
  boxTypeOption(VehicleBoxType.HARDWARE_GIANT_CRANE_BOX, "HARDWARE_GIANT_CRANE_BOX"),
  boxTypeOption(
    VehicleBoxType.HARDWARE_TOW_PLATE_WITH_CRANE,
    "HARDWARE_TOW_PLATE_WITH_CRANE"
  ),
  boxTypeOption(VehicleBoxType.CATTLE_TRANSPORT, "CATTLE_TRANSPORT"),
  boxTypeOption(
    VehicleBoxType.DISASSEMBLY_ASSEMBLY_EXISTING_VEHICLE,
    "DISASSEMBLY_ASSEMBLY_EXISTING_VEHICLE"
  ),
  boxTypeOption(
    VehicleBoxType.CURTAIN_ROOF_FOR_EXISTING,
    "CURTAIN_ROOF_FOR_EXISTING"
  ),
  boxTypeOption(
    VehicleBoxType.HYDRAULIC_WALL_INSTALLATION,
    "HYDRAULIC_WALL_INSTALLATION"
  ),
  boxTypeOption(VehicleBoxType.SKILLET, "SKILLET"),
  boxTypeOption(VehicleBoxType.REGULAR_SWAP_BODY, "REGULAR_SWAP_BODY"),
  boxTypeOption(VehicleBoxType.SWAP_CURTAIN_ROOF, "SWAP_CURTAIN_ROOF"),
  boxTypeOption(VehicleBoxType.SWAP_CLOSED_DRY, "SWAP_CLOSED_DRY"),
  boxTypeOption(
    VehicleBoxType.HYDRAULIC_WALL_DISASSEMBLY,
    "HYDRAULIC_WALL_DISASSEMBLY"
  ),
  boxTypeOption(VehicleBoxType.PURPOSE_CHANGE, "PURPOSE_CHANGE"),
  boxTypeOption(VehicleBoxType.CURTAIN_ROOF_PNEUMATIC, "CURTAIN_ROOF_PNEUMATIC"),
  boxTypeOption(VehicleBoxType.COMBINED, "COMBINED"),
  boxTypeOption(
    VehicleBoxType.HYDRAULIC_ROOF_POULTRY_TRANSPORT,
    "HYDRAULIC_ROOF_POULTRY_TRANSPORT"
  ),
  boxTypeOption(
    VehicleBoxType.OPEN_BOX_FORKLIFT_CARRIER,
    "OPEN_BOX_FORKLIFT_CARRIER"
  ),
  boxTypeOption(
    VehicleBoxType.CURTAIN_ROOF_FORKLIFT_CARRIER,
    "CURTAIN_ROOF_FORKLIFT_CARRIER"
  ),
  boxTypeOption(VehicleBoxType.OTHER, "OTHER"),
  boxTypeOption(VehicleBoxType.CANCELLED, "CANCELLED"),
];

export enum BoxFloorThickness {
  THICKNESS_2 = "THICKNESS_2",
  THICKNESS_3 = "THICKNESS_3",
  THICKNESS_4 = "THICKNESS_4",
  THICKNESS_5 = "THICKNESS_5",
  THICKNESS_6 = "THICKNESS_6",
  THICKNESS_8 = "THICKNESS_8",
  THICKNESS_40 = "THICKNESS_40",
}

const boxOption = (value: string, optionName: string): [string, string] => [
  value,
  `WORK_ORDER.BOX_DETAILS.FORM.OPTIONS.${optionName}`,
];

export const BOX_FLOOR_THICKNESS_SELECT_OPTIONS = [
  boxOption(BoxFloorThickness.THICKNESS_2, "FLOOR_THICKNESS_2"),
  boxOption(BoxFloorThickness.THICKNESS_3, "FLOOR_THICKNESS_3"),
  boxOption(BoxFloorThickness.THICKNESS_4, "FLOOR_THICKNESS_4"),
  boxOption(BoxFloorThickness.THICKNESS_5, "FLOOR_THICKNESS_5"),
  boxOption(BoxFloorThickness.THICKNESS_6, "FLOOR_THICKNESS_6"),
  boxOption(BoxFloorThickness.THICKNESS_8, "FLOOR_THICKNESS_8"),
  boxOption(BoxFloorThickness.THICKNESS_40, "FLOOR_THICKNESS_40"),
];

export enum BoxSubChassis {
  LSECT_70_70_190 = "LSECT_70_70_190",
}

export const BOX_SUB_CHASSIS_SELECT_OPTIONS = [
  boxOption(BoxSubChassis.LSECT_70_70_190, "SUB_CHASSIS_LSECT_70_70_190"),
];

export enum BoxFrontType {
  SHEET = "SHEET",
  SHEET_PIPE = "SHEET_PIPE",
  SHEET_WINDOW = "SHEET_WINDOW",
  REINFORCED_SHEET = "REINFORCED_SHEET",
  SHEET_PIPES = "SHEET_PIPES",
  INSULATED = "INSULATED",
  SHEET_PIPES_LONG = "SHEET_PIPES_LONG",
  SMOOTH_SHEET = "SMOOTH_SHEET",
  PLYWOOD = "PLYWOOD",
}

export const BOX_FRONT_TYPE_SELECT_OPTIONS = [
  boxOption(BoxFrontType.SHEET, "FRONT_TYPE_SHEET"),
  boxOption(BoxFrontType.SHEET_PIPE, "FRONT_TYPE_SHEET_PIPE"),
  boxOption(BoxFrontType.SHEET_WINDOW, "FRONT_TYPE_SHEET_WINDOW"),
  boxOption(BoxFrontType.REINFORCED_SHEET, "FRONT_TYPE_REINFORCED_SHEET"),
  boxOption(BoxFrontType.SHEET_PIPES, "FRONT_TYPE_SHEET_PIPES"),
  boxOption(BoxFrontType.INSULATED, "FRONT_TYPE_INSULATED"),
  boxOption(BoxFrontType.SHEET_PIPES_LONG, "FRONT_TYPE_SHEET_PIPES_LONG"),
  boxOption(BoxFrontType.SMOOTH_SHEET, "FRONT_TYPE_SMOOTH_SHEET"),
  boxOption(BoxFrontType.PLYWOOD, "FRONT_TYPE_PLYWOOD"),
];

export enum BoxRearWall {
  OPEN = "OPEN",
  CURTAIN = "CURTAIN",
  TWO_DOORS = "TWO_DOORS",
  CLOSED_SHEET = "CLOSED_SHEET",
  PIPES = "PIPES",
  TELESCOPIC = "TELESCOPIC",
  SPRING_UP = "SPRING_UP",
  ONE_DOOR = "ONE_DOOR",
  ROLLING_SHEET_SHUTTER = "ROLLING_SHEET_SHUTTER",
  WOOD_TRACK_SHUTTER = "WOOD_TRACK_SHUTTER",
  HINGED_SKATES = "HINGED_SKATES",
  SHEET = "SHEET",
  SHEET_PIPES_CRANE = "SHEET_PIPES_CRANE",
  REINFORCED_SHEET = "REINFORCED_SHEET",
  INSULATED = "INSULATED",
  SMOOTH_SHEET = "SMOOTH_SHEET",
}

export const BOX_REAR_WALL_SELECT_OPTIONS = [
  boxOption(BoxRearWall.OPEN, "REAR_WALL_OPEN"),
  boxOption(BoxRearWall.CURTAIN, "REAR_WALL_CURTAIN"),
  boxOption(BoxRearWall.TWO_DOORS, "REAR_WALL_TWO_DOORS"),
  boxOption(BoxRearWall.CLOSED_SHEET, "REAR_WALL_CLOSED_SHEET"),
  boxOption(BoxRearWall.PIPES, "REAR_WALL_PIPES"),
  boxOption(BoxRearWall.TELESCOPIC, "REAR_WALL_TELESCOPIC"),
  boxOption(BoxRearWall.SPRING_UP, "REAR_WALL_SPRING_UP"),
  boxOption(BoxRearWall.ONE_DOOR, "REAR_WALL_ONE_DOOR"),
  boxOption(BoxRearWall.ROLLING_SHEET_SHUTTER, "REAR_WALL_ROLLING_SHEET_SHUTTER"),
  boxOption(BoxRearWall.WOOD_TRACK_SHUTTER, "REAR_WALL_WOOD_TRACK_SHUTTER"),
  boxOption(BoxRearWall.HINGED_SKATES, "REAR_WALL_HINGED_SKATES"),
  boxOption(BoxRearWall.SHEET, "REAR_WALL_SHEET"),
  boxOption(BoxRearWall.SHEET_PIPES_CRANE, "REAR_WALL_SHEET_PIPES_CRANE"),
  boxOption(BoxRearWall.REINFORCED_SHEET, "REAR_WALL_REINFORCED_SHEET"),
  boxOption(BoxRearWall.INSULATED, "REAR_WALL_INSULATED"),
  boxOption(BoxRearWall.SMOOTH_SHEET, "REAR_WALL_SMOOTH_SHEET"),
];

export enum BoxRearWallType {
  RUBBER_BANDS = "RUBBER_BANDS",
  PATENT = "PATENT",
  RATCHET = "RATCHET",
  INSULATED_40 = "INSULATED_40",
  INSULATED_60 = "INSULATED_60",
  INSULATED_80 = "INSULATED_80",
  X2_LARGE = "X2_LARGE",
  X2_SMALL = "X2_SMALL",
  LARGE = "LARGE",
  SMALL = "SMALL",
  CLOSE = "CLOSE",
  WOOD = "WOOD",
  SHEET = "SHEET",
  PROFILES = "PROFILES",
}

export const BOX_REAR_WALL_TYPE_SELECT_BY_WALL: {
  [rearWall: string]: [string, string][];
} = {
  [BoxRearWall.CURTAIN]: [
    boxOption(BoxRearWallType.RUBBER_BANDS, "REAR_WALL_TYPE_RUBBER_BANDS"),
    boxOption(BoxRearWallType.PATENT, "REAR_WALL_TYPE_PATENT"),
    boxOption(BoxRearWallType.RATCHET, "REAR_WALL_TYPE_RATCHET"),
  ],
  [BoxRearWall.TWO_DOORS]: [
    boxOption(BoxRearWallType.INSULATED_40, "REAR_WALL_TYPE_INSULATED_40"),
    boxOption(BoxRearWallType.INSULATED_60, "REAR_WALL_TYPE_INSULATED_60"),
    boxOption(BoxRearWallType.INSULATED_80, "REAR_WALL_TYPE_INSULATED_80"),
  ],
  [BoxRearWall.SPRING_UP]: [
    boxOption(BoxRearWallType.X2_LARGE, "REAR_WALL_TYPE_X2_LARGE"),
    boxOption(BoxRearWallType.X2_SMALL, "REAR_WALL_TYPE_X2_SMALL"),
    boxOption(BoxRearWallType.LARGE, "REAR_WALL_TYPE_LARGE"),
    boxOption(BoxRearWallType.SMALL, "REAR_WALL_TYPE_SMALL"),
  ],
  [BoxRearWall.ONE_DOOR]: [
    boxOption(BoxRearWallType.CLOSE, "REAR_WALL_TYPE_CLOSE"),
    boxOption(BoxRearWallType.WOOD, "REAR_WALL_TYPE_WOOD"),
    boxOption(BoxRearWallType.SHEET, "REAR_WALL_TYPE_SHEET"),
    boxOption(BoxRearWallType.PROFILES, "REAR_WALL_TYPE_PROFILES"),
  ],
};

export enum BoxPillars {
  REGULAR = "REGULAR",
  U120 = "U120",
  LIGHT = "LIGHT",
  GAS = "GAS",
  EXTERNAL_2F_MID_QTY_2F = "EXTERNAL_2F_MID_QTY_2F",
  EXTERNAL_2F_MID_QTY_2R = "EXTERNAL_2F_MID_QTY_2R",
}

export const BOX_PILLARS_SELECT_OPTIONS = [
  boxOption(BoxPillars.REGULAR, "PILLARS_REGULAR"),
  boxOption(BoxPillars.U120, "PILLARS_U120"),
  boxOption(BoxPillars.LIGHT, "PILLARS_LIGHT"),
  boxOption(BoxPillars.GAS, "PILLARS_GAS"),
  boxOption(BoxPillars.EXTERNAL_2F_MID_QTY_2F, "PILLARS_EXTERNAL_2F_MID_QTY_2F"),
  boxOption(BoxPillars.EXTERNAL_2F_MID_QTY_2R, "PILLARS_EXTERNAL_2F_MID_QTY_2R"),
];

export enum BoxSideDoors {
  WOOD_500 = "WOOD_500",
  WOOD_600 = "WOOD_600",
  PROFILE_400 = "PROFILE_400",
  PROFILE_500 = "PROFILE_500",
  PROFILE_600 = "PROFILE_600",
  PROFILE_800 = "PROFILE_800",
  SHEET_400 = "SHEET_400",
  SHEET_500 = "SHEET_500",
  SHEET_600 = "SHEET_600",
  TWO_GAS_PIPES = "TWO_GAS_PIPES",
  ONE_GAS_PIPE = "ONE_GAS_PIPE",
  TWO_PROFILES_60_40_325 = "TWO_PROFILES_60_40_325",
  ONE_PROFILE_60_40_325 = "ONE_PROFILE_60_40_325",
  EXTERNAL_400 = "EXTERNAL_400",
  EXTERNAL_500 = "EXTERNAL_500",
  EXTERNAL_600 = "EXTERNAL_600",
  PROFILE_LADDERS = "PROFILE_LADDERS",
  TWO_SIDES_CLOSE = "TWO_SIDES_CLOSE",
}

export const BOX_SIDE_DOORS_OPTIONS = [
  boxOption(BoxSideDoors.WOOD_500, "SIDE_DOORS_WOOD_500"),
  boxOption(BoxSideDoors.WOOD_600, "SIDE_DOORS_WOOD_600"),
  boxOption(BoxSideDoors.PROFILE_400, "SIDE_DOORS_PROFILE_400"),
  boxOption(BoxSideDoors.PROFILE_500, "SIDE_DOORS_PROFILE_500"),
  boxOption(BoxSideDoors.PROFILE_600, "SIDE_DOORS_PROFILE_600"),
  boxOption(BoxSideDoors.PROFILE_800, "SIDE_DOORS_PROFILE_800"),
  boxOption(BoxSideDoors.SHEET_400, "SIDE_DOORS_SHEET_400"),
  boxOption(BoxSideDoors.SHEET_500, "SIDE_DOORS_SHEET_500"),
  boxOption(BoxSideDoors.SHEET_600, "SIDE_DOORS_SHEET_600"),
  boxOption(BoxSideDoors.TWO_GAS_PIPES, "SIDE_DOORS_TWO_GAS_PIPES"),
  boxOption(BoxSideDoors.ONE_GAS_PIPE, "SIDE_DOORS_ONE_GAS_PIPE"),
  boxOption(BoxSideDoors.TWO_PROFILES_60_40_325, "SIDE_DOORS_TWO_PROFILES_60_40_325"),
  boxOption(BoxSideDoors.ONE_PROFILE_60_40_325, "SIDE_DOORS_ONE_PROFILE_60_40_325"),
  boxOption(BoxSideDoors.EXTERNAL_400, "SIDE_DOORS_EXTERNAL_400"),
  boxOption(BoxSideDoors.EXTERNAL_500, "SIDE_DOORS_EXTERNAL_500"),
  boxOption(BoxSideDoors.EXTERNAL_600, "SIDE_DOORS_EXTERNAL_600"),
  boxOption(BoxSideDoors.PROFILE_LADDERS, "SIDE_DOORS_PROFILE_LADDERS"),
  boxOption(BoxSideDoors.TWO_SIDES_CLOSE, "SIDE_DOORS_TWO_SIDES_CLOSE"),
];

export const BOX_REAR_DOOR_OPTIONS = [
  boxOption(BoxSideDoors.WOOD_500, "REAR_DOOR_WOOD_500"),
  boxOption(BoxSideDoors.WOOD_600, "REAR_DOOR_WOOD_600"),
  boxOption(BoxSideDoors.PROFILE_400, "REAR_DOOR_PROFILE_400"),
  boxOption(BoxSideDoors.PROFILE_500, "REAR_DOOR_PROFILE_500"),
  boxOption(BoxSideDoors.PROFILE_600, "REAR_DOOR_PROFILE_600"),
  boxOption(BoxSideDoors.PROFILE_800, "REAR_DOOR_PROFILE_800"),
  boxOption(BoxSideDoors.SHEET_400, "REAR_DOOR_SHEET_400"),
  boxOption(BoxSideDoors.SHEET_500, "REAR_DOOR_SHEET_500"),
  boxOption(BoxSideDoors.SHEET_600, "REAR_DOOR_SHEET_600"),
  boxOption(BoxSideDoors.TWO_GAS_PIPES, "REAR_DOOR_TWO_GAS_PIPES"),
  boxOption(BoxSideDoors.ONE_GAS_PIPE, "REAR_DOOR_ONE_GAS_PIPE"),
  boxOption(BoxSideDoors.TWO_PROFILES_60_40_325, "REAR_DOOR_TWO_PROFILES_60_40_325"),
  boxOption(BoxSideDoors.ONE_PROFILE_60_40_325, "REAR_DOOR_ONE_PROFILE_60_40_325"),
  boxOption(BoxSideDoors.EXTERNAL_400, "REAR_DOOR_EXTERNAL_400"),
  boxOption(BoxSideDoors.EXTERNAL_500, "REAR_DOOR_EXTERNAL_500"),
  boxOption(BoxSideDoors.EXTERNAL_600, "REAR_DOOR_EXTERNAL_600"),
  boxOption(BoxSideDoors.PROFILE_LADDERS, "REAR_DOOR_PROFILE_LADDERS"),
  boxOption(BoxSideDoors.TWO_SIDES_CLOSE, "REAR_DOOR_TWO_SIDES_CLOSE"),
];

export enum BoxRoofMaterial {
  FIBER = "FIBER",
  GALVANIZED_SHEET = "GALVANIZED_SHEET",
  INSULATED_40MM_FIBER = "INSULATED_40MM_FIBER",
  INSULATED_60MM_FIBER = "INSULATED_60MM_FIBER",
  INSULATED_60MM_SHEET = "INSULATED_60MM_SHEET",
  INSULATED_80MM_FIBER = "INSULATED_80MM_FIBER",
  INSULATED_100MM_FIBER = "INSULATED_100MM_FIBER",
  PLYWOOD = "PLYWOOD",
}

export const BOX_ROOF_MATERIAL_OPTIONS = [
  boxOption(BoxRoofMaterial.FIBER, "ROOF_MATERIAL_FIBER"),
  boxOption(BoxRoofMaterial.GALVANIZED_SHEET, "ROOF_MATERIAL_GALVANIZED_SHEET"),
  boxOption(BoxRoofMaterial.INSULATED_40MM_FIBER, "ROOF_MATERIAL_INSULATED_40MM_FIBER"),
  boxOption(BoxRoofMaterial.INSULATED_60MM_FIBER, "ROOF_MATERIAL_INSULATED_60MM_FIBER"),
  boxOption(BoxRoofMaterial.INSULATED_60MM_SHEET, "ROOF_MATERIAL_INSULATED_60MM_SHEET"),
  boxOption(BoxRoofMaterial.INSULATED_80MM_FIBER, "ROOF_MATERIAL_INSULATED_80MM_FIBER"),
  boxOption(BoxRoofMaterial.INSULATED_100MM_FIBER, "ROOF_MATERIAL_INSULATED_100MM_FIBER"),
  boxOption(BoxRoofMaterial.PLYWOOD, "ROOF_MATERIAL_PLYWOOD"),
];

export enum BoxSideRail {
  REGULAR_EXTERNAL = "REGULAR_EXTERNAL",
  RAISED_EXTERNAL = "RAISED_EXTERNAL",
  INTERNAL = "INTERNAL",
  BOTTOM_HYDRAULIC_ROOF = "BOTTOM_HYDRAULIC_ROOF",
}

export const BOX_SIDE_RAIL_OPTIONS = [
  boxOption(BoxSideRail.REGULAR_EXTERNAL, "SIDE_RAIL_REGULAR_EXTERNAL"),
  boxOption(BoxSideRail.RAISED_EXTERNAL, "SIDE_RAIL_RAISED_EXTERNAL"),
  boxOption(BoxSideRail.INTERNAL, "SIDE_RAIL_INTERNAL"),
  boxOption(BoxSideRail.BOTTOM_HYDRAULIC_ROOF, "SIDE_RAIL_BOTTOM_HYDRAULIC_ROOF"),
];

export const BOX_REAR_RAIL_OPTIONS = [
  boxOption(BoxSideRail.REGULAR_EXTERNAL, "REAR_RAIL_REGULAR_EXTERNAL"),
  boxOption(BoxSideRail.RAISED_EXTERNAL, "REAR_RAIL_RAISED_EXTERNAL"),
  boxOption(BoxSideRail.INTERNAL, "REAR_RAIL_INTERNAL"),
];

export interface WorkOrderVehicleDetails {
  workOrderNo: string;
  customerName: string;
  subCustomer: string;
  chassis: string;
  manufacturer: string;
  model: string;
  axleStructure: AxleStructureEnum;
  year: string;
  boxType: BoxTypeOption;
}

export enum DiagramDepartmentTab {
  DIAGRAM = 0,
  BENDING = 1,
  CUTTING = 2,
  FRONT_REAR_WALL = 3,
  ROOF = 4,
}

export type DiagramInfoField = {
  labelKey: string;
  value: string;
};

export type DiagramSectionLayout = "default" | "columnFlow" | "singleColumn";

export type DiagramSection = {
  titleKey: string;
  fields: DiagramInfoField[];
  layout: DiagramSectionLayout;
};

export type DiagramPageLayout =
  | "overview"
  | "sections"
  | "toolsAndNotes"
  | "singleSection";

export type DiagramCustomNotes = {
  titleKey: string;
  notes: string[];
};

export type DiagramPageData = {
  layout?: DiagramPageLayout;
  projectInfo?: DiagramInfoField[];
  customerVehicle?: DiagramInfoField[];
  technicalSpec?: DiagramInfoField[];
  sections?: DiagramSection[];
  section?: DiagramSection;
  toolboxesAndAccessories?: DiagramInfoField[];
  customNotes?: DiagramCustomNotes;
};

export type DiagramViewState = {
  activeDepartment: DiagramDepartmentTab;
  activePage: number;
  pages: DiagramPageData[];
};

export enum WorkOrderDetailTab {
  PRICE_QUOTE = 0,
  VEHICLE_DETAILS = 1,
  CENTER_OF_GRAVITY = 2,
  BOX_DETAILS = 3,
  ADDONS = 4,
  WEIGHT_DATA = 5,
  LOAD_DISTRIBUTION = 6,
  PICTURES = 7,
}

export type BoxDetailsFormValues = {
  recommended: string;
  minimum: string;
  frontSpacing: string;
  minLength: string;
  maxLength: string;
  generalLength: string;
  boxLength: string;
  centerOfGravity: string;
  centerOfGravityTo: string;
  centerOfGravityRange: string;
  maxRearOverhang: string;
  rearOverhangPlus: string;
  rearOverhang: string;
  floorSideHeight: string;
  floorType: string;
  floor: string;
  maxWidth: string;
  boxWidth: string;
  floorHeight: string;
  crossbeams: string;
  subChassis: string;
  rearWallType: string;
  rearWall: string;
  frontHeight: string;
  frontType: string;
  sideDoorsQuantity: string;
  sideDoors: string;
  pillarsQuantity: string;
  pillars: string;
  comment: string;
  clampingLocks: boolean;
  rearDoor: string;
  sideRail: string;
  roofBeam: string;
  roofMaterial: string;
  panelInternal: string;
  maximum: string;
  sideLightOpening: string;
  rearLightOpening: string;
  hinge: string;
  rearRail: string;
};

export type AddOnItemFormValue = {
  enabled: boolean;
  selectedOption: string;
  note: string;
  quantity: string;
  size: string;
  location: string;
  itemType: string;
  optionEnabled: boolean;
};

export type AddOnFormValues = {
  sideGuard: AddOnItemFormValue;
  mudguard: AddOnItemFormValue;
  sideLighting: AddOnItemFormValue;
  interiorLighting: AddOnItemFormValue;
  rearLights: AddOnItemFormValue;
  heightLighting: AddOnItemFormValue;
  reverseProjector: AddOnItemFormValue;
  pipeMounts: AddOnItemFormValue;
  microphoneLights: AddOnItemFormValue;
  camera: AddOnItemFormValue;
  frontCableTensioner: AddOnItemFormValue;
  sideCableTensioner: AddOnItemFormValue;
  strapTensioners: AddOnItemFormValue;
  apcRings: AddOnItemFormValue;
  recessedRings: AddOnItemFormValue;
  loadSpreaderDevice: AddOnItemFormValue;
  waterJerrycan: AddOnItemFormValue;
  polesBasket: AddOnItemFormValue;
  palletJackMount: AddOnItemFormValue;
  spareMount: AddOnItemFormValue;
  rearLightProtection: AddOnItemFormValue;
  rearBumper: AddOnItemFormValue;
  frontCraneWings: AddOnItemFormValue;
  hazardousMaterials: AddOnItemFormValue;
  longTransportTriangle: AddOnItemFormValue;
  reflectors: AddOnItemFormValue;
  dieselLock: AddOnItemFormValue;
  batteriesLock: AddOnItemFormValue;
  ureaLock: AddOnItemFormValue;
  spareWheelLock: AddOnItemFormValue;
  hydraulicTailLiftLock: AddOnItemFormValue;
  hydraulicTailLiftMotorLock: AddOnItemFormValue;
  towHook: AddOnItemFormValue;
};

export type AddOnTableRow = {
  id: string;
  location: string;
  itemType: string;
  quantity: string;
  note: string;
  height: string;
};

export type AddOnTableKey = "toolboxes" | "openBasket" | "tyingStrip";

export type AddOnTablesState = Record<AddOnTableKey, AddOnTableRow[]>;

export type AddOnTyingStripModalState = {
  open: boolean;
  titleKey: string;
  height: string;
  note: string;
};

export type TableRowModalState = {
  open: boolean;
  tableKey: Extract<AddOnTableKey, "toolboxes" | "openBasket"> | null;
  titleKey: string;
  location: string;
  itemType: string;
  quantity: string;
  note: string;
};

export type AddOnDetailsState = {
  form: AddOnFormValues;
  tables: AddOnTablesState;
  tyingStripModal: AddOnTyingStripModalState;
  rowModal: TableRowModalState;
};

export type AddOnSelectOption = {
  value: string;
  labelKey: string;
};

const tableTypeKey = (name: string) =>
  `WORK_ORDER.ADD_ON.FORM.VALUES.TABLE_TYPE_${name}`;

const tableTypeOption = (name: string): AddOnSelectOption => ({
  value: tableTypeKey(name),
  labelKey: tableTypeKey(name),
});

export const TABLE_TYPE_OPTIONS: AddOnSelectOption[] = [
  tableTypeOption("WATERPROOF"),
  tableTypeOption("ABOVE_SPARE"),
];

export type WeightAndWallFormValues = {
  chassisWeight: string;
  netBox: string;
  frontWeighing: string;
  rearWeighing: string;
  generalWeighing: string;
  wallWeight: string;
  wallManufacturer: string;
  wallNumber: string;
};

export type LoadDistributionFormValues = {
  axleDistanceCalculation: string;
  centerOfGravityCalculation: string;
  offsetCalculation: string;
  rearAxle: string;
  generalSelf: string;
  frontAxle: string;
  fuelLevel: string;
  afterWeighingData: string;
  weighingComments: string;
  driverAndPassengers: string;
  wallType: string;
  hydraulicWallNumber: string;
};

export type LoadDistributionPdfTableRow = {
  labelKey: string;
  calculation: string;
  measurement: string;
};

export type LoadDistributionPdfFuelInfoField = {
  labelKey: string;
  valueKey: string;
  ltr?: boolean;
};

export type LoadDistributionPdfWeighingNote = {
  labelKey: string;
  valueKey?: string;
  value?: string;
};

export type LoadDistributionPdfHydraulicWallRow = {
  wallNumber: string;
  chassisNo: string;
};

export type LoadDistributionPdfWeightDistributionRow = {
  labelKey: string;
  front: string;
  rear: string;
  general: string;
};

export type LoadDistributionPdfData = {
  projectInfo: DiagramInfoField[];
  customerVehicle: DiagramInfoField[];
  technicalSpec: DiagramInfoField[];
  calculationTableRows: LoadDistributionPdfTableRow[];
  weighingNotes: LoadDistributionPdfWeighingNote[];
  hydraulicWall: {
    titleKey: string;
    columns: {
      wallNumberKey: string;
      chassisNoKey: string;
    };
    rows: LoadDistributionPdfHydraulicWallRow[];
  };
  weightDistribution: {
    titleKey: string;
    titleSuffixKey: string;
    columns: {
      detailKey: string;
      frontKey: string;
      rearKey: string;
      generalKey: string;
    };
    rows: LoadDistributionPdfWeightDistributionRow[];
  };
  manualWeighingForm: {
    rows: { valueKey: string }[];
  };
  fuelInfo: {
    fields: LoadDistributionPdfFuelInfoField[];
  };
};

const vehicleOption = (value: string, optionName: string): [string, string] => [
  value,
  `WORK_ORDER.VEHICLE_DETAILS.FORM.OPTIONS.${optionName}`,
];

export enum VehicleDriverCabEnum {
  SINGLE = "SINGLE",
  SLEEPING = "SLEEPING",
  DOUBLE_CREW = "DOUBLE_CREW",
  COMFORT = "COMFORT",
  DOUBLE = "DOUBLE",
}

export const VEHICLE_DRIVER_CAB_OPTIONS = [
  vehicleOption(VehicleDriverCabEnum.SINGLE, "DRIVER_CAB_SINGLE"),
  vehicleOption(VehicleDriverCabEnum.SLEEPING, "DRIVER_CAB_SLEEPING"),
  vehicleOption(VehicleDriverCabEnum.DOUBLE_CREW, "DRIVER_CAB_DOUBLE_CREW"),
  vehicleOption(VehicleDriverCabEnum.COMFORT, "DRIVER_CAB_COMFORT"),
  vehicleOption(VehicleDriverCabEnum.DOUBLE, "DRIVER_CAB_ORIGINAL_DOUBLE"),
];

export enum VehicleDriveEnum {
  FOUR_X2 = "4x2",
  FOUR_X4 = "4x4",
  SIX_X2 = "6x2",
  SIX_X4 = "6x4",
  EIGHT_X2 = "8x2",
  EIGHT_X4 = "8x4",
}

export const VEHICLE_DRIVE_OPTIONS = [
  vehicleOption(VehicleDriveEnum.FOUR_X2, "DRIVE_4X2"),
  vehicleOption(VehicleDriveEnum.FOUR_X4, "DRIVE_4X4"),
  vehicleOption(VehicleDriveEnum.SIX_X2, "DRIVE_6X2"),
  vehicleOption(VehicleDriveEnum.SIX_X4, "DRIVE_6X4"),
  vehicleOption(VehicleDriveEnum.EIGHT_X2, "DRIVE_8X2"),
  vehicleOption(VehicleDriveEnum.EIGHT_X4, "DRIVE_8X4"),
];

export enum VehicleRearSuspension {
  SPRING = "SPRING",
  PARABOLIC = "PARABOLIC",
  AIR = "AIR",
}

export const VEHICLE_REAR_SUSPENSION_OPTIONS = [
  vehicleOption(VehicleRearSuspension.SPRING, "REAR_SUSPENSION_SPRING"),
  vehicleOption(VehicleRearSuspension.PARABOLIC, "REAR_SUSPENSION_PARABOLIC"),
  vehicleOption(VehicleRearSuspension.AIR, "REAR_SUSPENSION_AIR"),
];

export type VehicleDetailsFormValues = {
  manufacturer: string;
  year: string;
  model: string;
  licenseNo: string;
  chassisNo: string;
  driverCab: string;
  passengers: string;
  drive: string;
  tire: string;
  rearSuspension: string;
  wheelbase: string;
  frontAxle: string;
  rearAxle: string;
  frontOverhang: string;
  chassisWidth: string;
  chassisHeight: string;
  totalHeight: string;
  chassisWeight: string;
  netBodyWeight: string;
  licenseWeight: string;
  frontLoad: string;
  rearLoad: string;
  hydraulic: string;
  transferBody: boolean;
};

export type QuotationFormValues = {
  totalSale: string;
  profitAmount: string;
  profitPercent: string;
  theoreticalPrice: string;
  totalCost: string;
  totalMaterialCost: string;
  workHours: string;
  perHour: string;
  totalWorkCost: string;
  repairHours: string;
  sheetMetalCost: string;
  craneCompletionAddition: string;
  exchangeRate: string;
  otherSaleItem: string;
  variableCoefficient: string;
  downPayment: string;
  vehicleWeightWithoutSide: string;
  assembledLength: string;
  assembledWidth: string;
  assembledHeight: string;
  floorBase: string;
  floorType: string;
  floorThickness: string;
  crossBeams: string;
  reinforcedModel: boolean;
  dollarRate: string;
  estimatedWeight: string;
  calculated: string;
  coefficients: string;
  curtainPart: string;
  curtainAndAdvertising: string;
  curtainWithoutBarrier: string;
  advertisingWithoutCurtain: string;
  withoutCurtainWithoutAdvertising: string;
  leasingInstallation: string;
  bodyHeightRange: string;
  adjustedHeight: string;
};

export type QuotationFrontWallFields = {
  enabled: boolean;
  selectedOption: string;
  height: string;
  treadSurface: boolean;
  treadNote: string;
  widthReinforcement: boolean;
  widthNote: string;
};

export type QuotationBoxStructureState = {
  frontWall: QuotationFrontWallFields;
  backWall: AddOnItemFormValue;
  frontRack: AddOnItemFormValue;
  backRack: AddOnItemFormValue;
  sideDoors: AddOnItemFormValue;
  pillars: AddOnItemFormValue;
  hydraulicSide: AddOnItemFormValue;
  rearDoor: AddOnItemFormValue;
};

export type QuotationRoofBoxStructureState = {
  roofType: AddOnItemFormValue;
  hydraulicRoof: AddOnItemFormValue;
  detachableRoof: AddOnItemFormValue;
  longitudinalBeams: AddOnItemFormValue;
  middlePillar: AddOnItemFormValue;
  frontWall: AddOnItemFormValue;
  rearFrame: AddOnItemFormValue;
  rearOpening: AddOnItemFormValue;
  hydraulicSide: AddOnItemFormValue;
  sideCompletionFlap: AddOnItemFormValue;
  sideDoors: AddOnItemFormValue;
  sidePillars: AddOnItemFormValue;
  plywoodInsteadOfCurtain: AddOnItemFormValue;
  curtainTypeAndTensioners: AddOnItemFormValue;
  tensionType: AddOnItemFormValue;
  curtainRail: AddOnItemFormValue;
  curtainEdge: AddOnItemFormValue;
};

export type QuotationRoofBoxCardKey = keyof QuotationRoofBoxStructureState;

export type QuotationClosedBoxStructureState = {
  roofType: AddOnItemFormValue;
  frontWall: AddOnItemFormValue;
  rearFrame: AddOnItemFormValue;
  frontWallReinforcement: AddOnItemFormValue;
  sideDoorOpenings: AddOnItemFormValue;
  rearOpening: AddOnItemFormValue;
  hydraulicSide: AddOnItemFormValue;
  sideCompletionFlap: AddOnItemFormValue;
};

export type QuotationClosedBoxCardKey = keyof QuotationClosedBoxStructureState;

export type ClosedBoxAccessoryCard = AddOnItemFormValue & {
  length: string;
  width: string;
  height: string;
};

export type QuotationClosedBoxAccessoriesState = {
  recessedRings: ClosedBoxAccessoryCard;
  apcRings: ClosedBoxAccessoryCard;
  toolbox: ClosedBoxAccessoryCard;
  palletJackMount: ClosedBoxAccessoryCard;
  openBasket: ClosedBoxAccessoryCard;
  jerrycan: ClosedBoxAccessoryCard;
  rearBumper: ClosedBoxAccessoryCard;
  spareWheelCarrier: ClosedBoxAccessoryCard;
  extinguisherMount: ClosedBoxAccessoryCard;
  hazmatSigns: ClosedBoxAccessoryCard;
  rearLightMounts: ClosedBoxAccessoryCard;
  lashingStrips: ClosedBoxAccessoryCard;
  wings: ClosedBoxAccessoryCard;
  hydraulicControlBoxLock: ClosedBoxAccessoryCard;
  ureaLock: ClosedBoxAccessoryCard;
  dieselLock: ClosedBoxAccessoryCard;
  sideDoorLock: ClosedBoxAccessoryCard;
  hydraulicSideLockEar: ClosedBoxAccessoryCard;
  batteriesLock: ClosedBoxAccessoryCard;
  sideGuardsReflectors: ClosedBoxAccessoryCard;
  sideLighting: ClosedBoxAccessoryCard;
  standardMudguards: ClosedBoxAccessoryCard;
  tyingStrips: AddOnTableRow[];
};

export type QuotationClosedBoxAccessoryCardKey = Exclude<
  keyof QuotationClosedBoxAccessoriesState,
  "tyingStrips"
>;

export type InsulatedBoxStructureCard = AddOnItemFormValue & {
  length: string;
};

export type QuotationInsulatedBoxStructureState = {
  roofType: InsulatedBoxStructureCard;
  frontWall: InsulatedBoxStructureCard;
  rearFrame: InsulatedBoxStructureCard;
  frontWallReinforcement: InsulatedBoxStructureCard;
  sideDoorOpenings: InsulatedBoxStructureCard;
  rearOpening: InsulatedBoxStructureCard;
  hydraulicSide: InsulatedBoxStructureCard;
  sideCompletionFlap: InsulatedBoxStructureCard;
};

export type QuotationInsulatedBoxCardKey =
  keyof QuotationInsulatedBoxStructureState;

export type InsulatedBoxAccessoryCard = AddOnItemFormValue & {
  length: string;
  width: string;
  height: string;
};

export type QuotationInsulatedBoxAccessoriesState = {
  recessedRings: InsulatedBoxAccessoryCard;
  apcRings: InsulatedBoxAccessoryCard;
  toolbox: InsulatedBoxAccessoryCard;
  openBasket: InsulatedBoxAccessoryCard;
  palletJackMount: InsulatedBoxAccessoryCard;
  jerrycan: InsulatedBoxAccessoryCard;
  rearBumper: InsulatedBoxAccessoryCard;
  rearLightMounts: InsulatedBoxAccessoryCard;
  hazmatSigns: InsulatedBoxAccessoryCard;
  extinguisherMount: InsulatedBoxAccessoryCard;
  spareWheelCarrier: InsulatedBoxAccessoryCard;
  lashingStrips: InsulatedBoxAccessoryCard;
  wings: InsulatedBoxAccessoryCard;
  coolingUnit: InsulatedBoxAccessoryCard;
  hydraulicControlBoxLock: InsulatedBoxAccessoryCard;
  ureaLock: InsulatedBoxAccessoryCard;
  dieselLock: InsulatedBoxAccessoryCard;
  sideDoorLock: InsulatedBoxAccessoryCard;
  hydraulicSideLockEar: InsulatedBoxAccessoryCard;
  batteriesLock: InsulatedBoxAccessoryCard;
  sideGuardsReflectors: InsulatedBoxAccessoryCard;
  sideLighting: InsulatedBoxAccessoryCard;
  standardMudguards: InsulatedBoxAccessoryCard;
  tyingStrips: AddOnTableRow[];
};

export type QuotationInsulatedBoxAccessoryCardKey = Exclude<
  keyof QuotationInsulatedBoxAccessoriesState,
  "tyingStrips"
>;

export type RoofBoxAccessoryCard = AddOnItemFormValue & {
  length: string;
  width: string;
  height: string;
};

export type QuotationRoofBoxAccessoriesState = {
  sideCableTensioner: RoofBoxAccessoryCard;
  strapTensioners: RoofBoxAccessoryCard;
  frontCableTensioner: RoofBoxAccessoryCard;
  recessedRings: RoofBoxAccessoryCard;
  apcRings: RoofBoxAccessoryCard;
  additionalTyingHooks: RoofBoxAccessoryCard;
  toolbox: RoofBoxAccessoryCard;
  openBasket: RoofBoxAccessoryCard;
  polesBasket: RoofBoxAccessoryCard;
  palletJackMount: RoofBoxAccessoryCard;
  polesStorageMount: RoofBoxAccessoryCard;
  jerrycanMount: RoofBoxAccessoryCard;
  rearBumper: RoofBoxAccessoryCard;
  hazmatSigns: RoofBoxAccessoryCard;
  spareWheelCarrier: RoofBoxAccessoryCard;
  rearLightProtectionFrame: RoofBoxAccessoryCard;
  extinguisherMount: RoofBoxAccessoryCard;
  wings: RoofBoxAccessoryCard;
  roundIronSides: RoofBoxAccessoryCard;
  dieselLock: RoofBoxAccessoryCard;
  ureaLock: RoofBoxAccessoryCard;
  batteriesLock: RoofBoxAccessoryCard;
  rearLightMounts: RoofBoxAccessoryCard;
  sideLighting: RoofBoxAccessoryCard;
  microphoneLights: RoofBoxAccessoryCard;
  interiorLighting: RoofBoxAccessoryCard;
  projectors: RoofBoxAccessoryCard;
};

export type QuotationRoofBoxAccessoryCardKey =
  keyof QuotationRoofBoxAccessoriesState;

export type QuotationEquipmentTableKey = "toolboxes" | "openBasket";

export type QuotationTyingEquipmentState = {
  sideCableTensioner: AddOnItemFormValue;
  strapTensioners: AddOnItemFormValue;
  frontCableTensioner: AddOnItemFormValue;
  recessedRings: AddOnItemFormValue;
  apcRings: AddOnItemFormValue;
  additionalTyingHooks: AddOnItemFormValue;
  toolboxes: AddOnTableRow[];
  openBasket: AddOnTableRow[];
  polesBasket: AddOnItemFormValue;
  waterJerrycan: AddOnItemFormValue;
  palletJackMount: AddOnItemFormValue;
};

export type QuotationTyingEquipmentCardKey = Exclude<
  keyof QuotationTyingEquipmentState,
  QuotationEquipmentTableKey
>;

export type QuotationSafetyVehicleState = {
  spareWheelCarrier: AddOnItemFormValue;
  hazardousMaterialsMounts: AddOnItemFormValue;
  rearBumper: AddOnItemFormValue;
  couplingLocks: AddOnItemFormValue;
  extinguisherMounts: AddOnItemFormValue;
  rearLightMounts: AddOnItemFormValue;
  wings: AddOnItemFormValue;
  roundIronSides: AddOnItemFormValue;
};

export type QuotationLocksState = {
  dieselLock: AddOnItemFormValue;
  ureaLock: AddOnItemFormValue;
  batteriesLock: AddOnItemFormValue;
  hydraulicTailLiftMotorLock: AddOnItemFormValue;
  hydraulicTailLiftLock: AddOnItemFormValue;
  spareWheelLock: AddOnItemFormValue;
};

export type QuotationStandard95State = {
  sideGuardsReflectors: AddOnItemFormValue;
  sideLighting: AddOnItemFormValue;
  standardMudguards: AddOnItemFormValue;
};

export type CentreOfGravityStatus = "OK" | "NOT_OK";

export type CentreOfGravityAdjustmentMode = "SET_MAX_LENGTH" | "SET_MIN_VALUE";

export type CentreOfGravityFormValues = {
  calculatedValue: string;
  minRange: string;
  maxRange: string;
  centerOfGravityStatus: CentreOfGravityStatus;
  currentLength: string;
  minimalLength: string;
  maximalLength: string;
  vehicleLengthStatus: CentreOfGravityStatus;
  adjustmentMode: CentreOfGravityAdjustmentMode;
};

export type ChasisLoad = "pwo" | "pwf" | "pwr" | "swo" | "swf" | "swr";

export type ChassisLoadRow = {
  value: string;
  calculationValue: string;
};

export type ChassisLoadsState = Record<ChasisLoad, ChassisLoadRow>;
export type VehicleMeasureCode = "wb" | "cg" | "fo" | "g" | "k" | "rg" | "lbox" | "hist" | "dg";
export type VehicleMeasuresState = Record<VehicleMeasureCode, ChassisLoadRow>;
export type AdditionalEquipmentCode = "np1" | "np2" | "wr" | "rcg" | "wc" | "ccg";
export type AdditionalEquipmentState = Record<AdditionalEquipmentCode, ChassisLoadRow>;

export interface WorkOrderState {
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  statusFilter: WorkOrderStatusFilter[];
  workOrders: WorkOrderTableRow[];
  selectedWorkOrderId: number | null;
  vehicleDetails: WorkOrderVehicleDetails | null;
  vehicleDetailsForm: VehicleDetailsFormValues | null;
  boxDetailsForm: BoxDetailsFormValues | null;
  weightAndWallForm: WeightAndWallFormValues | null;
  loadDistributionForm: LoadDistributionFormValues | null;
  addOnDetails: AddOnDetailsState | null;
  quotationForm: QuotationFormValues | null;
  quotationBoxStructure: QuotationBoxStructureState | null;
  quotationRoofBoxStructure: QuotationRoofBoxStructureState | null;
  quotationClosedBoxStructure: QuotationClosedBoxStructureState | null;
  quotationClosedBoxAccessories: QuotationClosedBoxAccessoriesState | null;
  quotationInsulatedBoxStructure: QuotationInsulatedBoxStructureState | null;
  quotationInsulatedBoxAccessories: QuotationInsulatedBoxAccessoriesState | null;
  quotationRoofBoxAccessories: QuotationRoofBoxAccessoriesState | null;
  quotationTyingEquipment: QuotationTyingEquipmentState | null;
  quotationSafetyVehicle: QuotationSafetyVehicleState | null;
  quotationLocks: QuotationLocksState | null;
  quotationStandard95: QuotationStandard95State | null;
  centreOfGravityForm: CentreOfGravityFormValues | null;
  chassisLoads: ChassisLoadsState | null;
  vehicleMeasures: VehicleMeasuresState | null;
  additionalEquipment: AdditionalEquipmentState | null;
  diagramView: DiagramViewState;
  activeDetailTab: WorkOrderDetailTab;
}
